import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription, debounceTime, distinctUntilChanged, finalize } from 'rxjs';

import { AdminStudentPlatformHeaderComponent } from '../../admin-student-platform/admin-student-platform-header/admin-student-platform-header.component';
import { CollegePortalService } from '@edtech-ui/college-workspace/services/college-portal.service';
import { CollegeAccountStatus, CollegeListItem, CollegeProfile } from '@edtech-ui/college-workspace/models/college-portal.models';

@Component({
	selector: 'app-admin-college-approval',
	standalone: true,
	imports: [CommonModule, FormsModule, AdminStudentPlatformHeaderComponent],
	templateUrl: './admin-college-approval.component.html',
	styleUrls: ['./admin-college-approval.component.scss']
})
export class AdminCollegeApprovalComponent implements OnInit {
	colleges: CollegeListItem[] = [];
	selectedCollege: CollegeProfile | null = null;

	searchTerm = '';
	statusFilter = '';
	page = 1;
	limit = 10;
	total = 0;
	totalPages = 1;
	isLoading = false;
	isActionLoading = false;

	showRejectModal = false;
	showClarificationModal = false;
	rejectionReason = '';
	clarificationReason = '';
	actionTargetId: number | null = null;

	private searchSubject = new Subject<string>();
	private subscription = new Subscription();

	statusOptions: { label: string; value: string }[] = [
		{ label: 'All', value: '' },
		{ label: 'Pending', value: 'pending' },
		{ label: 'Approved', value: 'approved' },
		{ label: 'Rejected', value: 'rejected' },
		{ label: 'Blocked', value: 'blocked' },
		{ label: 'Needs Clarification', value: 'need_clarification' },
	];

	constructor(
		private toastr: ToastrService,
		private collegeService: CollegePortalService
	) {}

	ngOnInit(): void {
		this.subscription.add(
			this.searchSubject.pipe(debounceTime(500), distinctUntilChanged()).subscribe(() => {
				this.page = 1;
				this.loadColleges();
			})
		);
		this.loadColleges();
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	onSearchChange(): void {
		this.searchSubject.next(this.searchTerm);
	}

	onStatusFilterChange(): void {
		this.page = 1;
		this.loadColleges();
	}

	loadColleges(): void {
		this.isLoading = true;
		this.collegeService.getAllCollegesList({
			status: this.statusFilter || undefined,
			search: this.searchTerm?.trim() || undefined,
			page: this.page,
			limit: this.limit,
		}).pipe(
			finalize(() => { this.isLoading = false; })
		).subscribe({
			next: (res) => {
				if (res.Success && res.Data) {
					this.colleges = res.Data.colleges;
					this.total = res.Data.total;
					this.totalPages = res.Data.total_pages;
				} else {
					this.toastr.error(res.Message || 'Failed to load colleges.');
				}
			},
			error: () => {
				this.toastr.error('Failed to load colleges.');
			}
		});
	}

	goToPage(page: number): void {
		if (page < 1 || page > this.totalPages) return;
		this.page = page;
		this.loadColleges();
	}

	viewDetails(collegeId: number): void {
		this.collegeService.getCollegeById(collegeId).subscribe({
			next: (res) => {
				if (res.Success && res.Data) {
					this.selectedCollege = res.Data;
				} else {
					this.toastr.error(res.Message || 'Failed to fetch college details.');
				}
			},
			error: () => this.toastr.error('Failed to fetch college details.')
		});
	}

	closeDetails(): void {
		this.selectedCollege = null;
	}

	approve(collegeId: number): void {
		this.isActionLoading = true;
		this.collegeService.postCollegeApprovalAction(collegeId, { action: 'approve' }).pipe(
			finalize(() => { this.isActionLoading = false; })
		).subscribe({
			next: (res) => {
				if (res.Success) {
					this.toastr.success(res.Message || 'College approved.');
					this.loadColleges();
				} else {
					this.toastr.error(res.Message || 'Failed to approve college.');
				}
			},
			error: (err) => this.toastr.error(err.error?.Message || 'Failed to approve college.')
		});
	}

	openRejectModal(collegeId: number): void {
		this.actionTargetId = collegeId;
		this.rejectionReason = '';
		this.showRejectModal = true;
	}

	confirmReject(): void {
		if (!this.actionTargetId || this.rejectionReason.trim().length < 5) {
			this.toastr.error('Please enter a rejection reason (at least 5 characters).');
			return;
		}
		this.isActionLoading = true;
		this.collegeService.postCollegeApprovalAction(this.actionTargetId, {
			action: 'reject', rejection_reason: this.rejectionReason.trim()
		}).pipe(
			finalize(() => { this.isActionLoading = false; })
		).subscribe({
			next: (res) => {
				if (res.Success) {
					this.toastr.success(res.Message || 'College rejected.');
					this.showRejectModal = false;
					this.loadColleges();
				} else {
					this.toastr.error(res.Message || 'Failed to reject college.');
				}
			},
			error: (err) => this.toastr.error(err.error?.Message || 'Failed to reject college.')
		});
	}

	openClarificationModal(collegeId: number): void {
		this.actionTargetId = collegeId;
		this.clarificationReason = '';
		this.showClarificationModal = true;
	}

	confirmClarification(): void {
		if (!this.actionTargetId || this.clarificationReason.trim().length < 5) {
			this.toastr.error('Please enter what clarification is needed (at least 5 characters).');
			return;
		}
		this.isActionLoading = true;
		this.collegeService.postCollegeApprovalAction(this.actionTargetId, {
			action: 'need_clarification', clarification_reason: this.clarificationReason.trim()
		}).pipe(
			finalize(() => { this.isActionLoading = false; })
		).subscribe({
			next: (res) => {
				if (res.Success) {
					this.toastr.success(res.Message || 'Clarification requested.');
					this.showClarificationModal = false;
					this.loadColleges();
				} else {
					this.toastr.error(res.Message || 'Failed to request clarification.');
				}
			},
			error: (err) => this.toastr.error(err.error?.Message || 'Failed to request clarification.')
		});
	}

	toggleBlock(college: CollegeListItem): void {
		const action = college.status === 'blocked' ? 'approve' : 'block';
		this.isActionLoading = true;
		this.collegeService.postCollegeApprovalAction(college.id, { action }).pipe(
			finalize(() => { this.isActionLoading = false; })
		).subscribe({
			next: (res) => {
				if (res.Success) {
					this.toastr.success(res.Message || 'College status updated.');
					this.loadColleges();
				} else {
					this.toastr.error(res.Message || 'Failed to update college status.');
				}
			},
			error: (err) => this.toastr.error(err.error?.Message || 'Failed to update college status.')
		});
	}

	statusBadgeClass(status: CollegeAccountStatus): string {
		switch (status) {
			case 'approved': return 'badge-approved';
			case 'rejected': return 'badge-rejected';
			case 'blocked': return 'badge-blocked';
			case 'need_clarification': return 'badge-clarification';
			default: return 'badge-pending';
		}
	}
}
